/* eslint no-use-before-define: "error" */
import * as HotspotType from "utils/HotspotType";
import extractImageUrl from "utils/extractImageUrl";
import extractVideoRoot from "utils/extractVideoRoot";
import generateId from "utils/generateId";
import { BLANK_VIDEO_DURATION, BLANK_VIDEO_SRC } from "constants/BlankVideo";
import { BLANK_VIDEO_ID } from "constants/DefaultIds";
import cloneDeep from "lodash/cloneDeep";
import defaults from "lodash/defaults";
import find from "lodash/find";
import get from "lodash/get";
import isArray from "lodash/isArray";
import mapValues from "lodash/mapValues";
import omit from "lodash/omit";
import set from "lodash/set";
import { normalize, Schema, arrayOf, unionOf } from "normalizr";

function reduceItem(item) {
  [
    "thumbnail",
    "video",
    "image",
    "idleVideo",
    "introductionVideo.image",
    "introductionVideo.video",
    "loopVideo.image",
    "loopVideo.video",
  ].forEach((key) => {
    if (isArray(get(item, key))) {
      set(item, key, get(item, key)[0]);
    }
  });

  if (item.video) {
    item.video.src = extractVideoRoot(item.video.src[0]);
  }

  if (item.idleVideo) {
    item.idleVideo.src = extractVideoRoot(item.idleVideo.src[0]);
  }

  if (item.image) {
    item.image.src = extractImageUrl(item.image.src);
  }

  if (item.thumbnail) {
    item.thumbnail.src = extractImageUrl(item.thumbnail.src);
  }

  if (item.duration && item.video) {
    item.video.duration = item.duration;
  }

  if (item.duration && item.idleVideo) {
    item.idleVideo.duration = item.duration;
  }

  if (item.loopVideo && item.loopVideo.video) {
    const videoRoot = extractVideoRoot(item.loopVideo.video.src[0]);
    item.loopVideo.video.src = videoRoot;

    // Add `firstFrame` so we it'll get added to `entities.images`
    item.loopVideo.firstFrame = {
      ...item.loopVideo.video,
      mediaType: "image",
      src: `/av/${videoRoot}/PNG/frame_0000.png`,
    };
  }

  if (item.loopVideo && item.loopVideo.image) {
    item.loopVideo.image.src = extractImageUrl(item.loopVideo.image.src);
  }

  if (item.introductionVideo && item.introductionVideo.video) {
    item.introductionVideo.video.src = extractVideoRoot(
      item.introductionVideo.video.src[0],
    );
  }

  if (item.introductionVideo && item.introductionVideo.image) {
    item.introductionVideo.image.src = extractImageUrl(
      item.introductionVideo.image.src,
    );
  }

  ["cuepoints", "slides", "questions", "chapters"].forEach((key) => {
    const length = get(item, `[${key}].length`);

    if (length) {
      item[key].forEach(reduceItem);
    }
  });
}

function patchLoopVideos(normalizedData) {
  const patchedData = cloneDeep(normalizedData);
  patchedData.entities.videos[BLANK_VIDEO_ID] = {
    duration: BLANK_VIDEO_DURATION,
    focalX: 0.5,
    focalY: 0.5,
    height: 1080,
    isBlank: true,
    mediaType: "video",
    src: BLANK_VIDEO_SRC,
    width: 1920,
  };

  if (!patchedData.video) {
    patchedData.video = BLANK_VIDEO_ID;
  }

  patchedData.entities.qaChapter = mapValues(
    patchedData.entities.qaChapter,
    (chap) => {
      if (!chap.idleVideo) {
        chap.idleVideo = BLANK_VIDEO_ID;
      }
      return chap;
    },
  );

  patchedData.entities.pathfinderChapter = mapValues(
    patchedData.entities.pathfinderChapter,
    (chap) => {
      if (chap.loopVideo && !chap.loopVideo.video) {
        chap.loopVideo.video = BLANK_VIDEO_ID;
      }
      return chap;
    },
  );
  return patchedData;
}

const automaticHotspot = new Schema("automaticHotspot", {
  idAttribute: "localId",
});
const invisibleHotspot = new Schema("invisibleHotspot", {
  idAttribute: "localId",
});
const tagHotspot = new Schema("tagHotspot", { idAttribute: "localId" });

const hotspot = unionOf(
  {
    automaticHotspot,
    invisibleHotspot,
    tagHotspot,
  },
  {
    schemaAttribute: ({ hotspotType: type, invisible }) => {
      if (type === HotspotType.TAG) {
        return "tagHotspot";
      }
      if (type === HotspotType.INVISIBLE || invisible) {
        return "invisibleHotspot";
      }
      if (type === HotspotType.AUTOMATIC) {
        return "automaticHotspot";
      }
      throw new Error(`unrecognized hotspot type ${type}`);
    },
  },
);

const cuepoint = new Schema("cuepoints", { idAttribute: "localId" });

export function normalizeHotspot(rawData) {
  return normalize(rawData, hotspot);
}

export function normalizeCuepoint(rawData) {
  const normCuepoint = normalize(rawData, cuepoint);
  normCuepoint.entities.images = mapValues(
    normCuepoint.entities.images,
    (image) => image[0],
  );
  return normCuepoint;
}

export default function normalizeStory(rawData) {
  const data = cloneDeep(rawData);

  data.chapters = data.chapterStore;
  delete data.chapterStore;

  reduceItem(data);

  const story = new Schema("story");

  const question = new Schema("questions");
  const slide = new Schema("slide");
  const image = new Schema("images", {
    idAttribute: (entity) => entity.id || generateId(),
  });
  const video = new Schema("videos", {
    idAttribute: (entity) => entity.id || generateId(),
  });
  const cuepointTarget = new Schema("cuepointTargets", {
    idAttribute: generateId,
  });
  const textTrack = new Schema("textTracks", {
    idAttribute: generateId,
  });

  // Chapter types
  const videoChapter = new Schema("videoChapter");
  const qaChapter = new Schema("qaChapter");
  const slideshowChapter = new Schema("slideshowChapter");
  const pathfinderChapter = new Schema("pathfinderChapter");

  video.define({
    textTracks: arrayOf(textTrack),
  });

  const defaultChapterFields = {
    cuepoints: arrayOf(cuepoint),
    hotspots: arrayOf(hotspot),
    image,
    thumbnail: image,
  };

  qaChapter.define({
    ...defaultChapterFields,
    idleVideo: video,
    questions: arrayOf(question),
  });
  slideshowChapter.define({
    ...defaultChapterFields,
    slides: arrayOf(slide),
  });
  videoChapter.define({
    ...defaultChapterFields,
    video,
  });

  pathfinderChapter.define({
    ...defaultChapterFields,
    introductionVideo: {
      image,
      video,
    },
    loopVideo: {
      firstFrame: image,
      image,
      video,
    },
  });

  const chapterUnion = {
    pathfinderChapter,
    qaChapter,
    slideshowChapter,
    videoChapter,
  };

  const chapter = unionOf(chapterUnion, {
    schemaAttribute: ({ type }) => {
      switch (type) {
        case "video":
          return "videoChapter";
        case "slideshow":
          return "slideshowChapter";
        case "q&a":
        case "qa":
        case "explainer":
        case "interview":
          return "qaChapter";
        case "pathfinder":
          return "pathfinderChapter";
        default:
          throw new Error(`unrecognized chapter type ${type}`);
      }
    },
  });

  story.define({
    chapters: arrayOf(chapter),
    thumbnail: image,
    video,
  });

  cuepoint.define({
    target: cuepointTarget,
    thumbnail: image,
  });

  slide.define({
    image,
    video,
  });

  question.define({
    questions: arrayOf(question),
    image,
    video,
  });

  const normalizedData = normalize(data, story);

  const storyEntity = normalizedData.entities.story[normalizedData.result];

  let result = {
    entities: defaults(
      {},
      omit(normalizedData.entities, "topLevelChapterIds", "story"),
      {
        cuepoints: [],
        cuepointTargets: [],
        pathfinderChapter: [],
        qaChapter: [],
        slide: [],
        slideshowChapter: [],
        videoChapter: [],
      },
    ),
    ...omit(storyEntity, "topLevelChapterIds"),
    topLevelChapters: storyEntity.topLevelChapterIds.map((chapterId) =>
      find(storyEntity.chapters, { id: chapterId }),
    ),
  };

  //These are all non-blank videos
  result.entities.videos = mapValues(result.entities.videos, (vid) => {
    vid.resolutions = vid.resolutions || [];
    vid.isBlank = false;
    return vid;
  });

  result = patchLoopVideos(result);

  return result;
}
